import React, { useEffect, useState, useMemo } from "react";
import { connect } from "react-redux";
import { Button, Alert } from "reactstrap";
import * as providerActions from "../../store/actions";
import { useSelector, useDispatch } from "react-redux";
import ProviderModal from "./ProviderModal";
import EditFormModal from "./EditFormModal";
import TableContainer from "components/Common/TableContainer";
import { Pdate } from "./AccCol";
import { toast } from "react-toastify";

const ProvidersList = () => {
  const { serviceProviders } = useSelector((state) => state.serviceProviders);
  const dispatch = useDispatch();
  const [selectedProviderData, setSelectedProviderData] = useState(null);
  const [editData, setEditData] = useState(null);
  const [modal1, setModal1] = useState(false);
  const [modal2, setModal2] = useState(false);
  const [cancellationFilter, setCancellationFilter] = useState('all'); // 'all', 'critical', 'warning'

  useEffect(() => {
    dispatch(providerActions.fetchServiceProvidersRequest());
  }, [dispatch]);

  // Calculate cancellation statistics
  const cancellationStats = useMemo(() => {
    if (!serviceProviders) return { critical: 0, warning: 0, total: 0 };
    
    const critical = serviceProviders.filter(p => (p.order_cancellation_count || 0) >= 8).length;
    const warning = serviceProviders.filter(p => {
      const count = p.order_cancellation_count || 0;
      return count >= 4 && count < 8;
    }).length;
    const total = critical + warning;
    
    return { critical, warning, total };
  }, [serviceProviders]);

  // Show toast notification on load if there are critical cases
  useEffect(() => {
    if (cancellationStats.critical > 0) {
      toast.error(
        `⚠️ تحذير عاجل: يوجد ${cancellationStats.critical} مزود خدمة بحاجة إلى متابعة فورية بسبب ارتفاع معدل الإلغاء`,
        {
          position: "top-center",
          autoClose: 8000,
          hideProgressBar: false,
        }
      );
    } else if (cancellationStats.warning > 0) {
      toast.warning(
        `📊 تنبيه: يوجد ${cancellationStats.warning} مزود خدمة يحتاج إلى مراقبة معدل الإلغاء`,
        {
          position: "top-center",
          autoClose: 6000,
        }
      );
    }
  }, [cancellationStats.critical, cancellationStats.warning]);

  const handleEditProviderModal = (service) => {
    setEditData(service);
    setModal2(true);
  };

  const handleRemoveProvider = (id) => {
    // Show a custom confirmation toast
    toast(
      <div>
        <p>سيتم حذف مزود الخدمة هذا، هل أنت متأكد أنك تريد المتابعة؟</p>
        <Button
          className="btn-primary m-3"
          onClick={() => {
            dispatch(providerActions.removeServiceProviderRequest(id));
            toast.dismiss();
          }}
        >
          مواصلة
        </Button>
        <Button
          className="btn-primary"
          onClick={() => {
            toast.dismiss();
          }}
        >
          الغاء
        </Button>
      </div>
    );
  };

  const toggleViewModal = (providerData) => {
    setSelectedProviderData(providerData);
    setModal1(!modal1);
  };

  const columns = useMemo(
    () => [
      {
        Header: "الطلبات الملغاة",
        accessor: "order_cancellation_count",
        disableFilters: true,
        sortType: "basic",
        sortDescFirst: true,
        Cell: ({ row }) => {
          const count = row.original.order_cancellation_count || 0;
          return (
            <div className="d-flex align-items-center justify-content-center">
              <span className={`badge ${getCancellationBadgeClass(count)} fs-6 px-3 py-2`}>
                <i className={`${getCancellationIcon(count)} me-1`}></i>
                {count}
              </span>
            </div>
          );
        },
      },
      {
        accessor: "email",
        disableFilters: false,
        filterable: true,
        Cell: (cellProps) => <Pdate {...cellProps} />,
      },
      {
        Header: "تحرير الحساب",
        disableFilters: true,
        Cell: ({ row }) => (
          <Button
            className="btn-primary"
            onClick={() => handleEditProviderModal(row.original)}
          >
            تحرير الحساب
          </Button>
        ),
      },
      {
        Header: "حذف الحساب",
        accessor: "reject",
        disableFilters: true,
        Cell: ({ row }) => (
          <Button
            className="btn-danger"
            onClick={() => handleRemoveProvider(row.original.id)}
          >
            حذف
          </Button>
        ),
      },
      {
        Header: "حالة الحساب",
        accessor: "action",
        disableFilters: true,
        Cell: ({ row }) => (
          <Button className={`${getStatusButtonClass(row.original.account_status)}`}>
            {translateStatus(row.original.account_status)}
          </Button>
        ),
      },
      {
        accessor: "description",
        disableFilters: true,
        Cell: ({ row }) => (
          <Button onClick={() => toggleViewModal(row.original)}>عرض التفاصيل</Button>
        ),
      },
    ],
    []
  );

  // Sort providers by cancellation count (highest to lowest)
  const sortedProviders = useMemo(() => {
    if (!serviceProviders) return [];
    return [...serviceProviders].sort((a, b) => {
      const countA = a.order_cancellation_count || 0;
      const countB = b.order_cancellation_count || 0;
      return countB - countA;
    });
  }, [serviceProviders]);

  // Filter providers based on cancellation filter
  const filteredProviders = useMemo(() => {
    if (cancellationFilter === 'critical') {
      return sortedProviders.filter(p => (p.order_cancellation_count || 0) >= 8);
    } else if (cancellationFilter === 'warning') {
      return sortedProviders.filter(p => {
        const count = p.order_cancellation_count || 0;
        return count >= 4 && count < 8;
      });
    }
    return sortedProviders;
  }, [sortedProviders, cancellationFilter]);

  return (
    <div>
      {/* Alert Banner for High Cancellation Cases */}
      {cancellationStats.total > 0 && (
        <div className="mb-3">
          {cancellationStats.critical > 0 && (
            <Alert color="danger" className="d-flex align-items-center justify-content-between mb-2">
              <div className="d-flex align-items-center">
                <i className="mdi mdi-alert-octagon me-2 fs-4"></i>
                <div>
                  <strong>تحذير عاجل - يتطلب إجراء فوري</strong>
                  <p className="mb-0 small">
                    يوجد {cancellationStats.critical} مزود خدمة لديهم معدل إلغاء مرتفع جداً (8+ طلبات ملغاة)
                  </p>
                </div>
              </div>
              <div 
                className="text-center" 
                style={{ cursor: 'pointer' }}
                onClick={() => setCancellationFilter('critical')}
                title="اضغط لعرض الحالات الحرجة فقط"
              >
                <div className="badge bg-white text-danger fs-4 px-3 py-2 hover-shadow">
                  <i className="mdi mdi-cursor-pointer me-1"></i>
                  {cancellationStats.critical}
                </div>
              </div>
            </Alert>
          )}
          
          {cancellationStats.warning > 0 && (
            <Alert color="warning" className="d-flex align-items-center justify-content-between mb-2">
              <div className="d-flex align-items-center">
                <i className="mdi mdi-alert me-2 fs-4"></i>
                <div>
                  <strong>تنبيه - يحتاج إلى مراقبة</strong>
                  <p className="mb-0 small">
                    يوجد {cancellationStats.warning} مزود خدمة لديهم معدل إلغاء متوسط (4-7 طلبات ملغاة)
                  </p>
                </div>
              </div>
              <div 
                className="text-center"
                style={{ cursor: 'pointer' }}
                onClick={() => setCancellationFilter('warning')}
                title="اضغط لعرض حالات التحذير فقط"
              >
                <div className="badge bg-white text-warning fs-4 px-3 py-2 hover-shadow">
                  <i className="mdi mdi-cursor-pointer me-1"></i>
                  {cancellationStats.warning}
                </div>
              </div>
            </Alert>
          )}

          {/* Statistics Dashboard Card */}
          <div className="card border-0 shadow-sm">
            <div className="card-body py-2">
              <div className="row text-center">
                <div 
                  className="col-4" 
                  style={{ cursor: 'pointer' }}
                  onClick={() => setCancellationFilter('critical')}
                  title="اضغط لعرض الحالات الحرجة"
                >
                  <div className={`text-danger ${cancellationFilter === 'critical' ? 'border border-danger rounded p-2' : ''}`}>
                    <i className="mdi mdi-alert-circle fs-3"></i>
                    <h4 className="mt-1 mb-0">{cancellationStats.critical}</h4>
                    <small className="text-muted">حرج</small>
                  </div>
                </div>
                <div 
                  className="col-4"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setCancellationFilter('warning')}
                  title="اضغط لعرض حالات التحذير"
                >
                  <div className={`text-warning ${cancellationFilter === 'warning' ? 'border border-warning rounded p-2' : ''}`}>
                    <i className="mdi mdi-alert fs-3"></i>
                    <h4 className="mt-1 mb-0">{cancellationStats.warning}</h4>
                    <small className="text-muted">تحذير</small>
                  </div>
                </div>
                <div 
                  className="col-4"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setCancellationFilter('all')}
                  title="اضغط لعرض جميع المزودين"
                >
                  <div className={`text-info ${cancellationFilter === 'all' ? 'border border-info rounded p-2' : ''}`}>
                    <i className="mdi mdi-account-group fs-3"></i>
                    <h4 className="mt-1 mb-0">{serviceProviders?.length || 0}</h4>
                    <small className="text-muted">الإجمالي</small>

                            {/* Active Filter Indicator */}
                            {cancellationFilter !== 'all' && (
                              <div className="alert alert-info mt-2 mb-0 d-flex align-items-center justify-content-between">
                                <div>
                                  <i className="mdi mdi-filter me-2"></i>
                                  <strong>تم التصفية:</strong> يتم عرض {cancellationFilter === 'critical' ? 'الحالات الحرجة' : 'حالات التحذير'} فقط ({filteredProviders.length} مزود)
                                </div>
                                <Button color="secondary" size="sm" onClick={() => setCancellationFilter('all')}>
                                  <i className="mdi mdi-close me-1"></i>
                                  إلغاء التصفية
                                </Button>
                              </div>
                            )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Table with Row Highlighting */}
      <div className="providers-table-wrapper">
        <style>{`
          .providers-table-wrapper .table tbody tr.critical-row {
            background-color: rgba(220, 53, 69, 0.1) !important;
            border-left: 4px solid #dc3545;
          }
          .providers-table-wrapper .table tbody tr.warning-row {
            background-color: rgba(255, 193, 7, 0.1) !important;
            border-left: 4px solid #ffc107;
          }
          .providers-table-wrapper .table tbody tr.critical-row:hover,
          .providers-table-wrapper .table tbody tr.warning-row:hover {
            background-color: rgba(255, 193, 7, 0.15) !important;
          }
          .hover-shadow:hover {
            transform: scale(1.05);
            box-shadow: 0 4px 8px rgba(0,0,0,0.2);
            transition: all 0.2s ease;
          }
        `}</style>
        <TableContainer
          columns={columns}
          data={filteredProviders}
          isGlobalFilter={false}
          customPageSize={5}
          customPageSizeOptions={true}
          customRowClass={(row) => {
            const count = row.order_cancellation_count || 0;
            if (count >= 8) return 'critical-row';
            if (count >= 4) return 'warning-row';
            return '';
          }}
        />
      </div>
      <ProviderModal
        isOpen={modal1}
        toggle={toggleViewModal}
        providerData={selectedProviderData}
      />
      <EditFormModal isOpen={modal2} toggle={() => setModal2(false)} editData={editData || {}} />
    </div>
  );
};

const mapStateToProps = (state) => ({
  ProvidersList: state.serviceProviders,
});
export default connect(mapStateToProps, providerActions)(ProvidersList);

const getStatusButtonClass = (accountStatus) => {
  switch (accountStatus) {
    case "active":
      return "btn-success";
    case "paused_for_subscription_expiry":
      return "btn-danger";
    case "action_required":
      return "btn-warning";
    default:
      return "btn-secondary";
  }
};

const translateStatus = (status) => {
  const translations = {
    active: "نشط",
    paused_for_subscription_expiry: "لانتهاء صلاحية الاشتراك",
    action_required: "غير نشط",
  };
  return translations[status] || status;
};

const getCancellationBadgeClass = (count) => {
  if (count === 0 || !count) return "bg-success";
  if (count <= 3) return "bg-info";
  if (count <= 7) return "bg-warning";
  return "bg-danger";
};

const getCancellationIcon = (count) => {
  if (count === 0 || !count) return "mdi mdi-check";
  if (count <= 3) return "mdi mdi-information-outline";
  if (count <= 7) return "mdi mdi-alert-outline";
  return "mdi mdi-alert-octagon-outline";
};
